import { apiClient } from '../utils/apiClient';

class OpportunityService {
  constructor() {
    this.baseUrl = '/opportunities';
  }

  /**
   * Get all active trading opportunities
   * @param {string[]} tokenAddresses - Optional array of token addresses to filter by
   * @param {number} minConfidence - Optional minimum confidence score (0-1)
   * @returns {Promise<Object>} Response with opportunities array
   */
  async getActiveOpportunities(tokenAddresses = null, minConfidence = null) {
    try {
      const params = new URLSearchParams();
      if (tokenAddresses && tokenAddresses.length > 0) {
        params.append('tokens', tokenAddresses.join(','));
      }
      if (minConfidence !== null && !isNaN(minConfidence)) {
        params.append('minConfidence', minConfidence.toString());
      }

      const queryString = params.toString();
      const url = `${this.baseUrl}/active${queryString ? `?${queryString}` : ''}`;

      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching active opportunities:', error);
      throw new Error('Failed to fetch trading opportunities');
    }
  }

  /**
   * Get trading opportunities relevant to the current user
   * @returns {Promise<Object>} Response with user-specific opportunities
   */
  async getUserOpportunities() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/user`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user opportunities:', error);
      throw new Error('Failed to fetch user trading opportunities');
    }
  }

  /**
   * Get opportunity statistics
   * @returns {Promise<Object>} Response with opportunity stats
   */
  async getOpportunityStats() {
    try {
      const response = await apiClient.get(`${this.baseUrl}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching opportunity stats:', error);
      throw new Error('Failed to fetch opportunity statistics');
    }
  }

  /**
   * Trigger manual opportunity scan (admin only)
   * @returns {Promise<Object>} Response confirming scan completion
   */
  async triggerScan() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/scan`);
      return response.data;
    } catch (error) {
      console.error('Error triggering opportunity scan:', error);
      throw new Error('Failed to trigger opportunity scan');
    }
  }

  /**
   * Start opportunity monitoring service (admin only)
   * @returns {Promise<Object>} Response confirming monitoring started
   */
  async startMonitoring() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/start-monitoring`);
      return response.data;
    } catch (error) {
      console.error('Error starting opportunity monitoring:', error);
      throw new Error('Failed to start opportunity monitoring');
    }
  }

  /**
   * Stop opportunity monitoring service (admin only)
   * @returns {Promise<Object>} Response confirming monitoring stopped
   */
  async stopMonitoring() {
    try {
      const response = await apiClient.post(`${this.baseUrl}/stop-monitoring`);
      return response.data;
    } catch (error) {
      console.error('Error stopping opportunity monitoring:', error);
      throw new Error('Failed to stop opportunity monitoring');
    }
  }

  /**
   * Create a new trading opportunity (admin only)
   * @param {Object} opportunityData - Opportunity data
   * @returns {Promise<Object>} Response with created opportunity
   */
  async createOpportunity(opportunityData) {
    try {
      const response = await apiClient.post(`${this.baseUrl}/create`, opportunityData);
      return response.data;
    } catch (error) {
      console.error('Error creating opportunity:', error);
      throw new Error('Failed to create trading opportunity');
    }
  }

  /**
   * Delete a trading opportunity (admin only)
   * @param {number} opportunityId - Opportunity ID to delete
   * @returns {Promise<Object>} Response confirming deletion
   */
  async deleteOpportunity(opportunityId) {
    try {
      const response = await apiClient.delete(`${this.baseUrl}/${opportunityId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting opportunity:', error);
      throw new Error('Failed to delete trading opportunity');
    }
  }

  /**
   * Get opportunities for specific tokens
   * @param {string[]} tokenAddresses - Array of token addresses
   * @returns {Promise<Object>} Response with filtered opportunities
   */
  async getOpportunitiesForTokens(tokenAddresses) {
    if (!tokenAddresses || tokenAddresses.length === 0) {
      return { success: true, data: { opportunities: [], count: 0 } };
    }
    
    return this.getActiveOpportunities(tokenAddresses);
  }

  /**
   * Check if a token has any active opportunities
   * @param {string} tokenAddress - Token address to check
   * @returns {Promise<boolean>} True if token has opportunities
   */
  async hasOpportunities(tokenAddress) {
    try {
      const response = await this.getActiveOpportunities([tokenAddress]);
      return response.success && response.data.opportunities.length > 0;
    } catch (error) {
      console.error('Error checking token opportunities:', error);
      return false;
    }
  }

  /**
   * Get the highest confidence opportunity for a token
   * @param {string} tokenAddress - Token address
   * @returns {Promise<Object|null>} Highest confidence opportunity or null
   */
  async getTopOpportunity(tokenAddress) {
    try {
      const response = await this.getActiveOpportunities([tokenAddress]);
      if (response.success && response.data.opportunities.length > 0) {
        // Sort by confidence score descending
        const sorted = response.data.opportunities.sort((a, b) => b.confidence_score - a.confidence_score);
        return sorted[0];
      }
      return null;
    } catch (error) {
      console.error('Error getting top opportunity:', error);
      return null;
    }
  }

  /**
   * Filter opportunities by type
   * @param {Object[]} opportunities - Array of opportunities
   * @param {string} type - Opportunity type to filter by
   * @returns {Object[]} Filtered opportunities
   */
  filterByType(opportunities, type) {
    return opportunities.filter(opp => opp.opportunity_type === type);
  }

  /**
   * Filter opportunities by minimum confidence score
   * @param {Object[]} opportunities - Array of opportunities
   * @param {number} minConfidence - Minimum confidence score (0-1)
   * @returns {Object[]} Filtered opportunities
   */
  filterByConfidence(opportunities, minConfidence = 0.5) {
    return opportunities.filter(opp => opp.confidence_score >= minConfidence);
  }

  /**
   * Group opportunities by token address
   * @param {Object[]} opportunities - Array of opportunities
   * @returns {Object} Opportunities grouped by token address
   */
  groupByToken(opportunities) {
    return opportunities.reduce((groups, opportunity) => {
      const tokenAddress = opportunity.token_address;
      if (!groups[tokenAddress]) {
        groups[tokenAddress] = [];
      }
      groups[tokenAddress].push(opportunity);
      return groups;
    }, {});
  }

  /**
   * Get opportunity summary for display
   * @param {Object} opportunity - Opportunity object
   * @returns {Object} Formatted summary
   */
  getOpportunitySummary(opportunity) {
    const typeLabels = {
      volume_spike: 'Volume Spike',
      price_breakout: 'Price Breakout',
      trend_reversal: 'Trend Reversal',
      social_buzz: 'Social Buzz'
    };

    const confidenceLevel = opportunity.confidence_score >= 0.8 ? 'High' :
                           opportunity.confidence_score >= 0.6 ? 'Medium' : 'Low';

    return {
      type: typeLabels[opportunity.opportunity_type] || 'Unknown',
      confidence: confidenceLevel,
      confidenceScore: opportunity.confidence_score,
      description: opportunity.description,
      priceChange: opportunity.price_change_1h,
      volumeChange: opportunity.volume_change_24h,
      expiresAt: new Date(opportunity.expires_at),
      isExpiringSoon: new Date(opportunity.expires_at) - new Date() < 60 * 60 * 1000 // 1 hour
    };
  }
}

// Create and export a singleton instance
const opportunityService = new OpportunityService();
export default opportunityService;